import { useEffect, useMemo, useRef, useState } from "react";
import { formatMoney } from "../lib/format";
import { useIsWide } from "../hooks/useIsDesktop";
import type { Category, Product } from "@cliqsuite/db/schemas";
import { useRows } from "../lib/db";
import { usePosShapeSyncReady } from "./PosDbProvider";

type SortKey = "Popular" | "Name A to Z" | "Price high" | "Price low";
const SORTS: SortKey[] = ["Popular", "Name A to Z", "Price high", "Price low"];

export interface MenuScreenProps {
  storeId: string;
  isDesktop: boolean;
  currency: { code: string; precision: 0 | 2 | 3 };
  /** Settings → Menu photos: picture tiles when true, text-only tiles when false. */
  showImages: boolean;
  cartQtyByProduct: Map<string, number>;
  onAdd: (product: Product) => void;
  /** Sold out ↔ back on the menu; asks for a manager PIN (PosApp). */
  onRequestToggleAvailability: (product: Product) => void;
  /** Hardware scanners type into the page; shown so staff know it's listening. */
  onOpenCameraScanner?: () => void;
}

// Tile tints when a category has no colour of its own (canvas palette).
const TINTS: Array<[string, string]> = [
  ["#F3ECE1", "#A88B63"],
  ["#E7F0E3", "#6F9164"],
  ["#FBE7E1", "#BF6B53"],
  ["#F8EADB", "#B9875B"],
  ["#ECEEF4", "#737D95"],
  ["#EAF0FB", "#4F6FA8"],
];

function tintFor(category: Category | undefined, index: number): [string, string] {
  if (category?.color) return [`${category.color}22`, category.color];
  return TINTS[index % TINTS.length]!;
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** Menu / product grid — catalog.categories + catalog.products from the local PowerSync copy. */
export function MenuScreen({ storeId, isDesktop, currency, showImages, cartQtyByProduct, onAdd, onRequestToggleAvailability, onOpenCameraScanner }: MenuScreenProps) {
  const categories = useRows("categories", "SELECT * FROM categories WHERE store_id = $1 AND deleted_at IS NULL", [storeId])?.rows;
  const productsResult = useRows("products", "SELECT * FROM products WHERE store_id = $1 AND deleted_at IS NULL", [storeId]);
  const products = productsResult?.rows;
  const isLoading = !usePosShapeSyncReady("products") || !productsResult;

  const [activeCat, setActiveCat] = useState<string>("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("Popular");
  const [sortOpen, setSortOpen] = useState(false);
  const now = useClock();
  const isWide = useIsWide();

  const sortedCategories = useMemo(
    () => (categories ?? []).filter((c) => c.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [categories],
  );
  const catIndex = useMemo(() => new Map(sortedCategories.map((c, i) => [c.id, i])), [sortedCategories]);

  // Raw/resale inventory items stay hidden until the owner turns them on; menu dishes show "Sold out" instead.
  const sellable = useMemo(() => (products ?? []).filter((p) => !p.is_inventory_item || p.is_available), [products]);
  const countByCat = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of sellable) if (p.category_id) m.set(p.category_id, (m.get(p.category_id) ?? 0) + 1);
    return m;
  }, [sellable]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(() => {
    const list = sellable.filter((p) =>
      q ? p.name.toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q) || (p.barcode ?? "").toLowerCase().includes(q) : activeCat === "All" || p.category_id === activeCat,
    );
    if (sort === "Price low") list.sort((a, b) => a.base_price - b.base_price);
    else if (sort === "Price high") list.sort((a, b) => b.base_price - a.base_price);
    else if (sort === "Name A to Z") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => Number(b.is_featured) - Number(a.is_featured) || a.sort_order - b.sort_order);
    return list;
  }, [sellable, q, activeCat, sort]);

  const cols = isDesktop ? 4 : isWide ? 3 : 2;
  const fmt = (v: number) => formatMoney(v, { currencyCode: currency.code, precision: currency.precision });

  return (
    <section aria-label="Menu" className={`pos-menu${isDesktop ? "" : " phone"}`}>
      <div className="pos-menu-top">
        <label className="pos-search">
          <span className="ms" aria-hidden="true">search</span>
          {/* A retail till scans with the camera key; a plugged-in scanner works on any till without a sign (canvas v2-d-sell, v2-d-retail-sell). */}
          <input
            aria-label={onOpenCameraScanner ? "Scan a barcode or type a SKU" : "Search the menu"}
            placeholder={onOpenCameraScanner ? "Scan a barcode or type a SKU" : "Search the menu"}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSortOpen(false);
            }}
          />
          {onOpenCameraScanner && (
            <button type="button" className="pos-search-cam" aria-label="Scan a barcode with the camera" onClick={onOpenCameraScanner}>
              <span className="ms" aria-hidden="true">photo_camera</span>
            </button>
          )}
        </label>
        {isDesktop && (
          <div className="pos-clock" aria-label="Time">
            <span className="ms" aria-hidden="true">schedule</span>
            <span>
              <b className="num">{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</b>
              <small>{now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</small>
            </span>
          </div>
        )}
      </div>

      <div className="pos-menu-bar">
        <div className="pos-pills">
          <button type="button" className={`pos-pill${!q && activeCat === "All" ? " on" : ""}`} onClick={() => setActiveCat("All")}>
            All <span>{sellable.length}</span>
          </button>
          {sortedCategories.map((c) => (
            <button key={c.id} type="button" className={`pos-pill${!q && activeCat === c.id ? " on" : ""}`} onClick={() => setActiveCat(c.id)}>
              {c.name} <span>{countByCat.get(c.id) ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="pos-sort">
          <button type="button" aria-haspopup="listbox" aria-expanded={sortOpen} className="pos-pill" onClick={() => setSortOpen((v) => !v)}>
            <span className="ms" aria-hidden="true">swap_vert</span>
            {sort}
            <span className="ms" aria-hidden="true">expand_more</span>
          </button>
          {sortOpen && (
            <div role="listbox" className="pos-menu-pop">
              {SORTS.map((o) => (
                <button
                  key={o}
                  role="option"
                  aria-selected={sort === o}
                  className={sort === o ? "on" : ""}
                  onClick={() => {
                    setSort(o);
                    setSortOpen(false);
                  }}
                >
                  {o}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pos-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {isLoading && <div className="pos-empty">Loading menu…</div>}
        {!isLoading && visible.length === 0 && (
          <div className="pos-empty">
            <span className="ms" aria-hidden="true">search_off</span>
            {q ? `Nothing matches "${search.trim()}"` : "No items in this category yet"}
          </div>
        )}
        {visible.map((p) => (
          <Tile
            key={p.id}
            product={p}
            qty={cartQtyByProduct.get(p.id) ?? 0}
            tint={tintFor(sortedCategories.find((c) => c.id === p.category_id), p.category_id ? (catIndex.get(p.category_id) ?? 0) : 4)}
            icon={sortedCategories.find((c) => c.id === p.category_id)?.icon ?? "restaurant_menu"}
            showImage={showImages}
            price={fmt(p.base_price)}
            onAdd={() => onAdd(p)}
            onToggle={() => onRequestToggleAvailability(p)}
          />
        ))}
      </div>
    </section>
  );
}

function Tile(props: {
  product: Product;
  qty: number;
  tint: [string, string];
  icon: string;
  showImage: boolean;
  price: string;
  onAdd: () => void;
  onToggle: () => void;
}) {
  const { product: p, qty } = props;
  const soldOut = !p.is_available;
  // Long-press (touch) or right-click marks an item sold out; tapping a sold-out tile asks to restore it.
  const press = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);
  const startPress = () => {
    longPressed.current = false;
    press.current = setTimeout(() => {
      longPressed.current = true;
      props.onToggle();
    }, 600);
  };
  const endPress = () => press.current && clearTimeout(press.current);
  return (
    <button
      type="button"
      className={`pos-tile${qty > 0 ? " in" : ""}`}
      aria-label={`${p.name}, ${soldOut ? "sold out" : props.price}${qty ? `, ${qty} on ticket` : ""}`}
      onClick={() => {
        if (longPressed.current) return;
        if (soldOut) props.onToggle();
        else props.onAdd();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        props.onToggle();
      }}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerLeave={endPress}
    >
      {props.showImage && (
        <span className={`pos-tile-pic${soldOut ? " off" : ""}`} style={{ background: props.tint[0] }}>
          {p.image_url ? (
            <img src={p.image_url} alt="" />
          ) : p.emoji ? (
            <span className="pos-tile-emoji">{p.emoji}</span>
          ) : (
            <span className="ms" aria-hidden="true" style={{ color: props.tint[1] }}>
              {props.icon}
            </span>
          )}
          {qty > 0 && <span className="pos-tile-qty">{qty}</span>}
        </span>
      )}
      <span className="pos-tile-body">
        <span className="pos-tile-name">
          <span>{p.name}</span>
          {p.is_new && <em>New</em>}
          {!props.showImage && qty > 0 && <span className="pos-tile-qty inline">{qty}</span>}
        </span>
        <span className="pos-tile-meta">
          {soldOut ? <span className="pos-tile-out">Sold out</span> : <span className="num">{props.price}</span>}
          {p.has_variants && !soldOut && (
            <span className="ms" aria-hidden="true">
              tune
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
